import { MAX_TEXT_LENGTH } from '../../utils/formula';
import { isFormulaMeasure, isPerItemMeasure } from './formulaModel';

/**
 * What the preview's RUN says about a chart's calculations, placed beside the
 * formula it is about (charts A8): the chart frame says it too, under the
 * plot, but the person fixing a formula is looking at the formula. The
 * builder cannot import the charts chunk's sentences (chartNotes.js — the
 * lazy boundary), so it says the server's own words, which the run carries.
 *
 *   formulaRunMessages(result, draft, view) -> [{ key, message: { tone, text } }]
 *
 * - `formula_error` notes (keepr-api SCHEMA "Formulas in a run"): items whose
 *   per-item formula could not be worked out — any warning from the formula
 *   module, the 10,000-character text cap among them — are left out and
 *   counted, the previous period's with "In the previous period". Said as a
 *   note under that measure's formula, with why an item can't be calculated.
 * - `formula_too_costly`, a failed run (both of the server's sentences: the
 *   chart's formulas priced past what one run may spend, or one item's past
 *   what one evaluation may): beside the one formula when there is one, else
 *   on the Measure step, since the server does not say which.
 */

/** Why an item's calculation can be left out — the formula module's warnings, the text cap among them. */
export const FORMULA_ERROR_WHY = `A calculation can't be worked out for an item when it divides by zero, reads a value of the wrong kind, or builds text longer than ${MAX_TEXT_LENGTH.toLocaleString('en-US')} characters.`;

// Said when the server sends no words of its own (chartNotes' fallback, word for word).
const COSTLY_FALLBACK = "This chart's calculations need more work than one run allows. Simplify the formula, or choose a narrower filter or a shorter range.";

const countOf = (n) => (typeof n === 'number' && Number.isFinite(n) ? n : 0);

function formulaErrorText(note, label) {
	const message = typeof note.message === 'string' ? note.message.trim() : '';
	if (message) return message;
	const n = countOf(note.count);
	const lead = note.period === 'previous' ? 'In the previous period, ' : '';
	const items = `${n.toLocaleString('en-US')} ${n === 1 ? 'item' : 'items'}`;
	const said = lead ? `${lead}${items}` : `${items.charAt(0).toUpperCase()}${items.slice(1)}`;
	return `${said} can't be calculated for ${label || 'this measure'}, so ${n === 1 ? "it isn't" : "they aren't"} included.`;
}

export function formulaRunMessages(result, draft, view) {
	const out = [];
	if (!result || !draft || !Array.isArray(draft.measures)) return out;
	const rows = (view && Array.isArray(view.measures)) ? view.measures : [];
	const formulaKeys = [];
	draft.measures.forEach((m, i) => {
		if (isPerItemMeasure(m)) formulaKeys.push(`measures.${i}.perItem`);
		else if (isFormulaMeasure(m)) formulaKeys.push(`measures.${i}.formula`);
	});
	if (result.ok === false) {
		const error = result.error || {};
		if (error.code === 'formula_too_costly') {
			const text = typeof error.message === 'string' && error.message.trim() ? error.message.trim() : COSTLY_FALLBACK;
			out.push({ key: formulaKeys.length === 1 ? formulaKeys[0] : 'measures', message: { tone: 'error', text } });
		}
		return out;
	}
	const explained = new Set();
	(Array.isArray(result.notes) ? result.notes : []).forEach((note) => {
		if (!note || note.code !== 'formula_error' || countOf(note.count) <= 0) return;
		const index = draft.measures.findIndex((m) => m && m.key === note.measure);
		if (index === -1 || !isPerItemMeasure(draft.measures[index])) return;
		const row = rows.find((r) => r.index === index);
		const label = (draft.measures[index].label && draft.measures[index].label.trim()) || (row && row.defaultLabel) || '';
		const key = `measures.${index}.perItem`;
		// Why, once per formula (the current period and the previous one may both be noted).
		const why = explained.has(key) ? '' : ` ${FORMULA_ERROR_WHY}`;
		explained.add(key);
		out.push({ key, message: { tone: 'note', text: `${formulaErrorText(note, label)}${why}` } });
	});
	return out;
}
